import React, { useState } from "react";
import { DrawerContent } from "../ui/Drawer";
import { Button } from "../ui/Button";
import { X } from "lucide-react";
import Divider from "dashboard/component/listDivider";
import FinanceBadge from "./FinanceBadge";
import { formatDate } from "../flex/utils";
import { financeStatus, financeTypeForDate, reversePaymentType } from "constants/global";
import { useMutation, useQueryClient } from "react-query";
import useUserDetails from "hooks/useUserDetails";
import { cancelFinanceRequest, downloadPaymentReceipt } from "api/credit-fetchers";
import { toast } from "react-toastify";
import { displayCurrencyAndAmount, financeTitles } from "constants/FinanceConstants";
import DeleteFinanceModal from "../flex/shipments/modals/DeleteFinanceModal";
import { Printer } from "iconsax-reactjs";
import { Link } from "react-router-dom";
import PoAndInvoicePreview from "pages/flex/Procurement/PoAndInvoicePreview";

const FinanceDrawer = ({ financeDetails, setOpen, isHistory = false }) => {
  const queryClient = useQueryClient();
  const { businessId } = useUserDetails();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPoAndInvoicePreview, setShowPoAndInvoicePreview] = useState(false);
  const [reason, setReason] = useState("");
  const { mutateAsync: handleCancelFinance, isLoading: isCancelingFinanceLoading } = useMutation({
    mutationFn: (id) => cancelFinanceRequest({ businessId, id, reason, type: "payment" }),
    onSettled: (response) => {
      if (response?.error === false) {
        toast.success(response?.message);
        queryClient?.invalidateQueries(["fetchAllFinancePay"]);
        setReason("");
        setShowDeleteModal(false);
      } else {
        toast.error(response?.response?.data?.message);
      }
    },
  });
  const { mutateAsync: handleDownloadReceipt, isLoading: isDownloadingReceipt } = useMutation({
    mutationFn: (id) => downloadPaymentReceipt({ paymentId: id }),
    onSettled: (response) => {
      if (response?.error === false) {
        toast.success(response?.message);
      } else {
        toast.error(response?.response?.data?.message);
      }
    },
  });
  const financeTypes = {
    inventory_finance: "inventoryFinance",
    payment_finance: "paymentFinance",
    payable_finance: "payableFinance",
    receivable_finance: "receivableFinance",
  };
  const financeCategory = financeTitles[financeDetails?.type];

  const initials =
    financeDetails?.[financeTypes[financeDetails?.type]]?.contractEmployer?.firstName?.at(0) +
    financeDetails?.[financeTypes[financeDetails?.type]]?.contractEmployer?.lastName?.at(0);

  const fullName =
    financeDetails?.[financeTypes[financeDetails?.type]]?.contractEmployer?.firstName +
    " " +
    financeDetails?.[financeTypes[financeDetails?.type]]?.contractEmployer?.lastName;
  const email = financeDetails?.[financeTypes[financeDetails?.type]]?.contractEmployer?.email;
  const invoiceUrlLink = financeDetails?.invoiceUrl;
  const hasPoOrInvoice = financeDetails?.purchaseOrder || financeDetails?.invoice

  return (
    <>
      <DrawerContent className="z-[999] data-[vault-drawer-direction=right]:sm:max-w-lg">
        <aside className="flex flex-col h-full p-3 bg-white rounded-l-md">
          {isHistory ? (
            <>
              <div className="flex items-center justify-between gap-2 py-2 mb-2">
                <p className="font-[450] whitespace-nowrap mb-1 text-light-grey-10 text-sm">
                  {financeTitles[financeDetails?.financeType] ||
                    "Finance Details"}
                </p>
                <div className="inline-flex w-full border" />

                <Button
                  onClick={() => {
                    setOpen(false);
                    setShowDeleteModal(false);
                  }}
                  size="sm"
                  variant="ghost"
                  className="px-0 text-sm text-gray-90"
                >
                  <X />
                </Button>
              </div>
              {["draft", "pending", "processing"].includes(
                financeDetails?.status
              ) && (
                  <Button
                    onClick={() => setShowDeleteModal(true)}
                    variant="outline"
                    size="sm"
                    className="my-2 font-normal"
                    disabled={
                      isCancelingFinanceLoading ||
                      financeDetails?.status === "cancelled"
                    }
                  >
                    {isCancelingFinanceLoading ? "Loading..." : "Cancel request"}
                  </Button>
                )}
              <div>
                <p className="text-xl font-medium text-gray-70">
                  {displayCurrencyAndAmount(
                    financeDetails?.currency,
                    financeDetails?.loanAmount ||
                    financeDetails?.inventoryFinance?.requestedAmount ||
                    financeDetails?.payableFinance?.requestedAmount ||
                    financeDetails?.receivableFinance?.requestedAmount
                  )}
                </p>
                <p className="mt-2 text-sm font-light text-gray-70">
                  Closed on{" "}
                  {formatDate(financeDetails?.createdAt, "MMM Do, YYYY")}
                </p>
                <div className="w-full gap-2 p-3 mt-3 rounded-md bg-bg-color-10">
                  {financeDetails?.type === Object.keys(financeTypes)[2] && (
                    <>
                      <p className="pb-1 mt-2 text-sm font-[450] ">
                        Contract Employer
                      </p>
                      <div className="mb-6 bg-white rounded-xl">
                        <div className="flex justify-between items-center !px-5 !py-4">
                          <div className="flex items-center justify-start gap-2">
                            <div className="flex items-center justify-center w-10 h-10 font-semibold text-[#475467] bg-[#EFF0FB] rounded-full">
                              {initials}
                            </div>
                            <div className="text-left">
                              <h3 className="text-lg font-medium mb-0 !text-left capitalize text-gray-90">
                                {fullName}
                              </h3>
                              <p className="text-xs font-light text-secondary-40">
                                {email}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-light text-gray-70">Status</p>
                    <p
                      className="text-sm font-light "
                      style={{
                        color: financeStatus[financeDetails?.status]
                          ?.textColor,
                      }}
                    >
                      {financeStatus[financeDetails?.status]?.text}
                    </p>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-light text-gray-70">
                      Amount requested
                    </p>
                    <p className="text-sm font-medium text-gray-70">
                      {displayCurrencyAndAmount(
                        financeDetails?.currency,
                        financeDetails?.loanAmount ||
                        financeDetails?.inventoryFinance
                          ?.requestedAmount ||
                        financeDetails?.payableFinance
                          ?.requestedAmount ||
                        financeDetails?.receivableFinance
                          ?.requestedAmount
                      )}
                    </p>
                  </div>
                  <Divider />
                  <div className="flex items-center justify-between my-2">
                    <p className="text-sm font-light text-gray-70">Category</p>
                    <p className="text-sm font-light text-gray-70">
                      {financeCategory}
                    </p>
                  </div>
                  <div className="flex items-center justify-between my-2">
                    <p className="text-sm font-light text-gray-70">
                      Completion Date
                    </p>
                    <p className="text-sm font-light text-gray-70">
                      {formatDate(
                        financeDetails?.disbursementDate,
                        "MMM Do, YYYY・h:mm A"
                      )}
                    </p>
                  </div>
                  <div className="flex items-center justify-between my-2">
                    <p className="text-sm font-light text-gray-70">
                      Request Date
                    </p>
                    <p className="text-sm font-light text-gray-70">
                      {formatDate(
                        financeDetails?.createdAt,
                        "MMM Do, YYYY・h:mm A"
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between gap-4 py-2 my-2">
                <div>
                  <p className="font-[450] mb-1 text-gray-90 text-sm"></p>
                  <p className="font-[450] mb-1 text-gray-90 text-sm">
                    {displayCurrencyAndAmount(
                      financeDetails?.currency,
                      financeDetails?.totalAmount
                    )}{" "}
                    <FinanceBadge
                      badgeType="Draft"
                      text={reversePaymentType[financeDetails?.paymentType]}
                    />
                  </p>
                  <p className="text-sm font-light text-gray-70">
                    {financeTypeForDate[financeDetails?.status]}{" "}
                    {formatDate(financeDetails?.bankStatement?.expiryDate)}
                  </p>
                </div>
                <div>
                  <Button
                    onClick={() => {
                      setOpen(false);
                    }}
                    size="sm"
                    variant="ghost"
                    className="text-sm text-gray-90"
                  >
                    <X />
                  </Button>
                </div>
              </div>

                {!["approved", "cancelled", "rejected"].includes(
                  financeDetails?.status
                ) && (
                    <Button
                      onClick={() => setShowDeleteModal(true)}
                      variant="outline"
                      size="sm"
                      className="my-2 font-normal"
                      disabled={
                        isCancelingFinanceLoading ||
                        financeDetails?.status === "cancelled"
                      }
                    >
                      {isCancelingFinanceLoading ? "Loading..." : "Cancel request"}
                    </Button>
                  )}
                {financeDetails?.status === "approved" && (
                  <Button
                    onClick={() => handleDownloadReceipt(financeDetails?._id)}
                    variant="outline"
                    size="sm"
                    className="my-2 font-normal"
                    disabled={isDownloadingReceipt}
                  >
                    <Printer />{" "}
                    {isDownloadingReceipt ? "Downloading..." : "Download receipt"}
                  </Button>
                )}

                <div className="w-full gap-3 p-3 mt-3 rounded-md bg-bg-color-10">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-light text-gray-70">Status</p>
                    <p
                      className="text-sm font-light "
                      style={{
                        color: financeStatus[financeDetails?.status]?.textColor,
                      }}
                    >
                      {financeStatus[financeDetails?.status]?.text}
                    </p>
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-light text-gray-70">
                      Amount requested
                    </p>
                    <p className="text-sm font-medium text-gray-70">
                      {displayCurrencyAndAmount(
                        financeDetails?.currency,
                        financeDetails?.totalAmount
                      )}
                    </p>
                  </div>
                  {financeDetails?.url && (
                    <>
                      <Divider />
                      <div className="flex items-center gap-3 justify-between my-3">
                        <p className="text-sm font-light whitespace-nowrap text-gray-70">Transaction Documents</p>
                        <p className="text-sm font-light truncate !text-secondary">
                          <Link to={financeDetails?.url} target="_blank" className="!text-secondary">
                            {financeDetails?.url}
                          </Link>
                        </p>
                      </div>
                    </>
                  )}
                  {invoiceUrlLink && (
                    <>
                      <Divider />
                      <div className="flex items-center gap-3 justify-between my-3">
                        <p className="text-sm font-light whitespace-nowrap text-gray-70">Invoice Document</p>
                        <p className="text-sm font-light truncate !text-secondary">
                          <Link to={invoiceUrlLink} target="_blank" className="!text-secondary">
                            {invoiceUrlLink}
                          </Link>
                        </p>
                      </div>
                    </>
                  )}
                  {hasPoOrInvoice && (
                    <>
                      <Divider />
                      <div className="flex items-center gap-3 justify-between my-3">
                        <p className="text-sm font-light whitespace-nowrap text-gray-70">{financeDetails?.purchaseOrder ? "Purchase orders" : "Invoice"}</p>
                        <p className="text-sm font-light truncate !text-secondary">
                          <Button onClick={() => {
                            setShowPoAndInvoicePreview(prev => !prev)
                          }} variant="ghost" className="p-0 !text-secondary">
                            {financeDetails?.purchaseOrder ? "Preview Purchase order" : "Preview Invoice"}
                          </Button>
                        </p>
                      </div>
                    </>
                  )}
                <Divider />
                <div className="flex items-center justify-between my-3">
                  <p className="text-sm font-light text-gray-70">Category</p>
                  <p className="text-sm font-light text-gray-70">
                    {financeCategory}
                  </p>
                </div>
                {(financeDetails?.status === 'disbursed' || financeDetails?.status === 'approved') && <div className="flex items-center justify-between my-2">
                  <p className="text-sm font-light text-gray-70">
                    {financeDetails?.status === 'approved' ? "Approved" : "Completion"} Date
                  </p>
                  <p className="text-sm font-light text-gray-70">
                    {formatDate(
                      financeDetails?.updatedAt,
                      "MMM Do, YYYY・h:mm A"
                    )}
                  </p>
                </div>}
                <div className="flex items-center justify-between my-2">
                  <p className="text-sm font-light text-gray-70">Request Date</p>
                  <p className="text-sm font-light text-gray-70">
                    {formatDate(
                      financeDetails?.createdAt,
                      "MMM Do, YYYY・h:mm A"
                    )}
                  </p>
                </div>
                <Divider />
                <div className="flex items-center justify-between my-3">
                  <p className="text-sm font-light text-gray-70">Bank</p>
                  <p className="text-sm font-light text-gray-70">
                    {financeDetails?.bankName}
                  </p>
                </div>
                <div className="flex items-center justify-between my-3">
                  <p className="text-sm font-light text-gray-70">
                    Account number
                  </p>
                  <p className="text-sm font-light text-gray-70">
                    {financeDetails?.accountNumber}
                  </p>
                </div>
                <div className="flex items-center justify-between my-3">
                  <p className="text-sm font-light text-gray-70">Account name</p>
                  <p className="text-sm font-light text-gray-70">
                    {financeDetails?.accountName}
                  </p>
                </div>
                <Divider />
                {financeDetails?.type === Object.keys(financeTypes)[2] && (
                  <>
                    <p className="pb-1 mt-2 text-sm font-[450] ">
                      Contract Employer
                    </p>
                    <div className="mb-6 bg-white rounded-xl">
                      <div className="flex justify-between items-center !px-5 !py-4">
                        <div className="flex items-center justify-start gap-2">
                          <div className="flex items-center justify-center w-10 h-10 font-semibold text-[#475467] bg-[#EFF0FB] rounded-full">
                            {initials}
                          </div>
                          <div className="text-left">
                            <h3 className="text-lg font-medium mb-0 !text-left capitalize text-gray-90">
                              {fullName}
                            </h3>
                            <p className="text-xs font-light text-secondary-40">
                              {email}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </aside>
      </DrawerContent>
      <DeleteFinanceModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        reason={reason}
        setReason={setReason}
        onSubmit={async (reason) => {
          await handleCancelFinance(financeDetails?._id);
        }}
        isLoading={isCancelingFinanceLoading}
      />
      {showPoAndInvoicePreview && <PoAndInvoicePreview
        show={showPoAndInvoicePreview}
        onClose={() => setShowPoAndInvoicePreview(false)}
        purchaseOrder={financeDetails?.purchaseOrder || financeDetails?.invoice}
      />}
    </>
  );
};

export default FinanceDrawer;
